import { apiService } from './api.service'
import { broadcastService } from './broadcast.service'
import { openDocumentsService } from './open-documents.service'
import { correspondentService } from './correspondent.service'
import { customFieldService } from './custom-field.service'
import { documentListViewService } from './document-list-view.service'
import { documentTypeService } from './document-type.service'
import { documentWorkflowSupportedActionService } from './document-workflow-supported-action.service'
import { savedViewService } from './saved-view.service'
import { storagePathService } from './storage-path.service'
import { tagService } from './tag.service'
import { toastService } from './toast.service'
import { userService } from './user.service'
import { websocketService } from './websocket.service'
import { workflowStatusService } from './workflow-status.service'

export function clearApplicationCaches(): void {
  websocketService.clearStatuses()
  websocketService.disconnect()
  toastService.clearToasts()
  documentListViewService.reset()
  savedViewService.clearCache()
  correspondentService.clearCache()
  customFieldService.clearCache()
  documentTypeService.clearCache()
  documentWorkflowSupportedActionService.clearCache()
  storagePathService.clearCache()
  tagService.clearCache()
  userService.clearCache()
  workflowStatusService.clearCache()
  openDocumentsService.resetAllOrganizationsForLogout()
}

class AuthService {
  private async ensureCsrfToken(): Promise<void> {
    if (!apiService.getCsrfToken()) {
      await apiService.get('csrf-token/')
    }
  }

  async login(
    username: string,
    password: string,
    mfaCode?: string
  ): Promise<{
    success: boolean
    organization_selection_required?: boolean
    organizations?: Array<{ id: number; name: string; is_default?: boolean }>
    currentOrganizationId?: number | null
  }> {
    await this.ensureCsrfToken()

    const payload: any = {
      username,
      password,
    }

    if (mfaCode) {
      payload.code = mfaCode
    }

    try {
      const response = await apiService.post('login/', payload)
      if (response.status === 200) {
        broadcastService.broadcast('login', {
          userId: response.data?.user?.id ?? null,
        })
        broadcastService.broadcast('session-changed')
      }
      return response.data
    } catch (error: any) {
      if (error.response?.status === 400) {
        const errorData = error.response.data
        if (errorData?.non_field_errors?.some((msg: string) => msg.includes('MFA code is required'))) {
          throw { mfa_required: true }
        }
        throw error
      }
      throw error
    }
  }

  async logout(): Promise<void> {
    try {
      await apiService.post('logout/', {})
    } catch (error) {
    } finally {
      clearApplicationCaches()
      localStorage.removeItem('ui_settings')
      document.cookie = 'csrftoken=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;'
      document.cookie = 'sessionid=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;'
      broadcastService.broadcast('logout')
    }
  }

  async setCurrentOrganization(organizationId: number): Promise<void> {
    await apiService.post('current-organization/', { organization_id: organizationId })
    openDocumentsService.setActiveOrganizationId(organizationId)
    websocketService.reconnectForOrgChange()
    broadcastService.broadcast('session-changed')
  }

  isAuthenticated(): boolean {
    return apiService.isAuthenticated()
  }
}

export const authService = new AuthService()
